import { useTranslation } from '@jq4race/i18n';
import type { TicketSearchResultItem } from '@jq4race/shared';
import { useNavigate } from '@tanstack/react-router';

import { SearchResultRow } from './SearchResultRow';

interface SearchResultListProps {
  items: TicketSearchResultItem[];
  query: string;
  /**
   * JQ4-54 (JQ4-38 follow-up) — opens the cash-payment dialog for a ticket id.
   * Wired per-row only for parked + unpaid tickets (gated in SearchResultRow too).
   */
  onRegisterCash?: (ticketId: string) => void;
  /**
   * JQ4-19 slice 3 — opens the comp dialog for a ticket id.
   * Wired per-row only for parked + unpaid tickets and when compsAuthorized=true.
   */
  onApplyComp?: (ticketId: string) => void;
  /** JQ4-19 slice 3 — Whether the actor is authorized to apply comps (Scenario 5). */
  compsAuthorized?: boolean;
}

export function SearchResultList({
  items,
  query,
  onRegisterCash,
  onApplyComp,
  compsAuthorized = false,
}: SearchResultListProps) {
  const { t } = useTranslation('search');
  const navigate = useNavigate();

  if (query.trim().length > 0 && items.length === 0) {
    return (
      <p className="py-8 text-center font-sans text-sm text-muted-foreground">{t('noResults')}</p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => (
        <SearchResultRow
          key={item.id}
          id={item.id}
          ticketNumber={item.ticketNumber}
          plate={item.plate}
          firstName={item.firstName}
          lastName={item.lastName}
          phone={item.phone}
          qrTag={item.qrTag}
          status={item.status}
          elapsedSeconds={item.elapsedSeconds}
          paymentStatus={item.paymentStatus}
          coveredByMembership={item.coveredByMembership}
          // JQ4-54 (JQ4-38 follow-up) — Cash button is ONLY wired for parked + unpaid tickets.
          // SearchResultRow is the primary gate (it re-checks status/paymentStatus); this ternary
          // is a convenience short-circuit so we don't bind a per-row closure when it won't be used.
          onRegisterCash={
            item.status === 'parked' && item.paymentStatus === 'unpaid' && onRegisterCash
              ? () => onRegisterCash(item.id)
              : undefined
          }
          // JQ4-19 slice 3 — Comp button wired for parked + unpaid + compsAuthorized (Scenario 5).
          onApplyComp={
            item.status === 'parked' &&
            item.paymentStatus === 'unpaid' &&
            compsAuthorized &&
            onApplyComp
              ? () => onApplyComp(item.id)
              : undefined
          }
          compsAuthorized={compsAuthorized}
          // JQ4-17 · US-CONFIG-003 — Exits button is ONLY shown for hotel-niche parked tickets (REQ-6.6).
          // locationNiche comes from the backend search result item and gates the feature correctly.
          onExitsClick={
            item.status === 'parked' && item.locationNiche === 'hotel'
              ? () =>
                  void navigate({
                    to: '/hotel-exits/$ticketId',
                    params: { ticketId: item.id },
                    search: { ticketNumber: item.ticketNumber, plate: item.plate },
                  })
              : undefined
          }
        />
      ))}
    </div>
  );
}
